import { useCallback, useEffect, useRef, useState } from 'react'
import { InboundNotificationListItem } from '@shared/core/inbound-sms'
import { inboundNotificationsService } from 'src/services/inbound-notifications.service'

const POLL_INTERVAL_MS = 30_000
const LIST_PAGE = 0
const LIST_LIMIT = 25

function countUnread(notifications: InboundNotificationListItem[]): number {
  return notifications.filter((notification) => notification.read_at == null).length
}

export function useInboundNotificationsPolling(enabled = true) {
  const [notifications, setNotifications] = useState<InboundNotificationListItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const sinceRef = useRef(new Date().toISOString())
  const hasLoadedListRef = useRef(false)
  const notificationsRef = useRef(notifications)

  useEffect(() => {
    notificationsRef.current = notifications
  }, [notifications])

  const fetchFullList = useCallback(async () => {
    const response = await inboundNotificationsService.list(LIST_PAGE, LIST_LIMIT)
    setNotifications(response.data ?? [])
    sinceRef.current = new Date().toISOString()
    hasLoadedListRef.current = true
  }, [])

  const pollChanges = useCallback(async () => {
    const changes = await inboundNotificationsService.getChanges(sinceRef.current)
    setUnreadCount(changes.unreadCount)
    const localUnreadCount = countUnread(notificationsRef.current)
    const shouldFetchList =
      changes.hasChanges ||
      !hasLoadedListRef.current ||
      (changes.unreadCount > 0 && localUnreadCount < changes.unreadCount)
    if (shouldFetchList) {
      await fetchFullList()
    }
  }, [fetchFullList])

  useEffect(() => {
    if (!enabled) {
      return undefined
    }
    void pollChanges()
    const timer = window.setInterval(() => {
      void pollChanges()
    }, POLL_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [enabled, pollChanges])

  const markRead = useCallback(async (id: string) => {
    await inboundNotificationsService.markRead(id)
    setNotifications((current) =>
      current.map((item) => (item.id === id ? { ...item, read_at: new Date().toISOString() } : item)),
    )
    setUnreadCount((count) => Math.max(0, count - 1))
  }, [])

  const markUnread = useCallback(async (id: string) => {
    await inboundNotificationsService.markUnread(id)
    setNotifications((current) => {
      const updated = current.map((item) => (item.id === id ? { ...item, read_at: null } : item))
      setUnreadCount(countUnread(updated))
      return updated
    })
  }, [])

  const markAllRead = useCallback(async () => {
    await inboundNotificationsService.markAllRead()
    const readAt = new Date().toISOString()
    setNotifications((current) => current.map((item) => (item.read_at == null ? { ...item, read_at: readAt } : item)))
    setUnreadCount(0)
  }, [])

  const toggleReadState = useCallback(
    async (id: string, isRead: boolean) => {
      if (isRead) {
        await markUnread(id)
      } else {
        await markRead(id)
      }
    },
    [markRead, markUnread],
  )

  return {
    notifications,
    unreadCount,
    markRead,
    markUnread,
    markAllRead,
    toggleReadState,
    refresh: fetchFullList,
  }
}
